import React, { ReactNode } from "react";

import { makePatch, makePost } from "@culturehq/client";

import { Form, ModalDialog, NumberField, SelectField, StringField, SubmitButton } from "@culturehq/components";
import { FormValues } from "@culturehq/components/dist/components/form/Form";

import { ExternalMapper } from "chq";
import { useProfile } from "app/context";
import { useConfirmClose, useDoor, useTryCatch } from "app/hooks";
import { t, Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";

const FLAG_OPTIONS = [
  { value: true, label: t("Yes") },
  { value: false, label: t("No") }
] as any;

const flagValidator = (value: any) => {
  if (value === true || value === false) {
    return null;
  }
  return "Required";
};

const ModalTitle: React.FC<{ externalMapper?: ExternalMapper }> = ({ externalMapper }) => {
  if (externalMapper) {
    return <Trans>Edit External Mapper</Trans>;
  }
  return <Trans>Add New External Mapper</Trans>;
};

interface ExternalMapperModalProps {
  children: (onOpen: () => void) => ReactNode;
  externalMapper?: ExternalMapper;
  onCreate?: (mapper: ExternalMapper) => void;
  onUpdate: (mapper: ExternalMapper) => void;
}

const ExternalMapperModal: React.FC<ExternalMapperModalProps> = ({
  children, externalMapper, onCreate, onUpdate
}) => {
  const { profile, fetchedProfile } = useProfile();
  const { open, onOpen, onClose } = useDoor(false);
  const { onChange, onConfirmClose, onSubmitClose } = useConfirmClose(
    "You have unsaved changes, are you sure you'd like to close the external mapper modal?",
    onClose
  );

  const [{ error }, onSubmit] = useTryCatch(
    async (values: FormValues) => {
      if (externalMapper) {
        const response: any = await makePatch(`/external_mappers/${externalMapper.id}`, values as any);
        fetchedProfile({ ...profile, organization: response.organization });
        onUpdate(response.externalMapper as ExternalMapper);
      } else {
        const response: any = await makePost("/external_mappers", values as any);
        fetchedProfile({ ...profile, organization: response.organization });
        if (onCreate) onCreate(response.externalMapper);
      }

      onSubmitClose();
    },
    [externalMapper, onClose, onCreate, onUpdate]
  );

  const validatePriority = (value: any) => {
    if (value < 0 || value > 1000) return "Not allowed value";
    return null;
  };

  if (error && error.status !== 422) {
    throw error;
  }

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onConfirmClose}>
          <ModalDialog.Heading onClose={onConfirmClose}>
            <ModalTitle externalMapper={externalMapper} />
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {error && <ErrorMessage error={error} />}
            <Form onSubmit={onSubmit as any} initialValues={externalMapper}>
              <StringField
                name="label"
                autoFocus
                required
                onChange={() => { onChange(); }}
              >
                <Trans>Label</Trans>
              </StringField>
              <NumberField
                className="ValidationWithLabel"
                name="priority"
                required
                min={0}
                max={1000}
                validator={validatePriority}
                onChange={() => { onChange(); }}
              >
                <Trans>Priority</Trans>
              </NumberField>
              <StringField
                name="icon"
                onChange={() => { onChange(); }}
              >
                <Trans>Icon</Trans>
              </StringField>
              <SelectField
                name="allowMultiple"
                validator={flagValidator}
                options={FLAG_OPTIONS}
                onChange={() => { onChange(); }}
              >
                <Trans>Multiselect Field</Trans>
              </SelectField>
              <SelectField
                name="creatableByRegularUsers"
                validator={flagValidator}
                options={FLAG_OPTIONS}
                onChange={() => { onChange(); }}
              >
                <Trans>Allow Users to Create new Values</Trans>
              </SelectField>
              <StringField
                name="externalHtmlIdentifier"
                required
                onChange={() => { onChange(); }}
              >
                <Trans>HTML Attribute ID</Trans>
              </StringField>
              <SubmitButton primary>
                {submitting => (submitting ? <Trans>Submitting...</Trans> : <Trans>Save</Trans>)}
              </SubmitButton>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default ExternalMapperModal;
